import type { CartItem, CartSummary } from '../types/cart.types'

/** All money values are integers (KES has no sub-cent) */
const round = (n: number) => Math.round(n)

/** 
 * Computes client-side CartSummary from current items.
 * NOTE: This is used for display only. Server totals are authoritative at checkout.
 */
export function computeCartSummary(items: CartItem[]): CartSummary {
  let subtotal = 0
  let totalSavings = 0
  let unitCount = 0

  for (const item of items) {
    // Only count valid or price_changed items in totals
    if (item.status === 'product_removed') continue

    const qty = item.quantity
    const unitPrice = item.unit_price
    const itemSubtotal = round(qty * unitPrice)
    subtotal += itemSubtotal

    // Savings = (compare_price - effective_price) × qty
    if (item.pricing.compare_price !== null && item.pricing.compare_price > unitPrice) {
      totalSavings += round(qty * (item.pricing.compare_price - unitPrice))
    }

    unitCount += qty
  }

  // VAT extracted from subtotal (assumes vat_included = true)
  // VAT rate for Kenya = 16%
  const VAT_RATE = 0.16
  const tax = round(subtotal * (VAT_RATE / (1 + VAT_RATE)))

  return {
    subtotal: round(subtotal),
    total_savings: round(totalSavings),
    shipping_estimate: 0, // Populated after address selection
    tax: round(tax),
    total: round(subtotal),
    currency: 'KES',
    item_count: items.filter((i) => i.status !== 'product_removed').length,
    unit_count: unitCount,
  }
}

export const formatKES = (n: number): string =>
  new Intl.NumberFormat('en-KE', {
    style: 'currency', currency: 'KES',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n)

export const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max)

/** Generates a v4-style UUID for guest cart item IDs */
export function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}